'use client';

/**
 * Cloudflare Turnstile — jeton CAPTCHA pour les appels Supabase Auth
 * (`signInWithOtp`, `signInAnonymously`) une fois la protection CAPTCHA
 * activée côté Supabase (Authentication → Attack Protection).
 *
 * DÉGRADATION VOLONTAIRE : si le script Cloudflare ne charge pas (bloqueur,
 * réseau, test) ou si le défi échoue / expire, `getToken()` résout `undefined`
 * et l'appelant envoie sa requête SANS jeton. Tant que le CAPTCHA est désactivé
 * côté Supabase, la requête passe donc exactement comme avant ; une fois
 * activé, c'est Supabase qui refuse (message d'erreur standard) — jamais un
 * blocage silencieux côté client.
 *
 * Mode `execution: 'execute'` + `appearance: 'interaction-only'` : rien n'est
 * affiché tant que Cloudflare ne demande pas d'interaction ; le défi est lancé
 * à la demande, juste avant l'appel Auth. Un jeton est à USAGE UNIQUE →
 * chaque `getToken()` réinitialise le widget (renvoi du code OTP inclus).
 */
import { useCallback, useEffect, useRef } from 'react';

/** Clé de site (publique) du widget « JeRaisonne - auth » — hostnames jeraisonne.fr (+ sous-domaines) et localhost. */
export const TURNSTILE_SITE_KEY = '0x4AAAAAAFFbkWWoSMPmU2iw';

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const SCRIPT_TIMEOUT_MS = 10_000;
const TOKEN_TIMEOUT_MS = 30_000;

export interface TurnstileApi {
  render(el: HTMLElement, options: Record<string, unknown>): string;
  execute(widgetId: string): void;
  reset(widgetId: string): void;
  remove(widgetId: string): void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let scriptPromise: Promise<TurnstileApi | null> | null = null;

/** Charge le script Cloudflare une seule fois ; `null` s'il ne charge pas à temps. */
export function loadTurnstile(timeoutMs = SCRIPT_TIMEOUT_MS): Promise<TurnstileApi | null> {
  if (typeof window === 'undefined') return Promise.resolve(null);
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise<TurnstileApi | null>((resolve) => {
    const timer = setTimeout(() => resolve(window.turnstile ?? null), timeoutMs);
    const done = () => {
      clearTimeout(timer);
      resolve(window.turnstile ?? null);
    };
    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = done;
    script.onerror = done;
    document.head.appendChild(script);
  }).then((api) => {
    // Échec : on autorise une nouvelle tentative au prochain montage.
    if (!api) scriptPromise = null;
    return api;
  });
  return scriptPromise;
}

/** Réservé aux tests : oublie le script déjà chargé. */
export function __resetTurnstileForTests() {
  scriptPromise = null;
}

export interface UseTurnstileOptions {
  siteKey?: string;
  /** Délai max d'un défi avant de renoncer (et d'envoyer sans jeton). */
  tokenTimeoutMs?: number;
}

export function useTurnstile({
  siteKey = TURNSTILE_SITE_KEY,
  tokenTimeoutMs = TOKEN_TIMEOUT_MS,
}: UseTurnstileOptions = {}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const apiRef = useRef<TurnstileApi | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const settleRef = useRef<((token: string | undefined) => void) | null>(null);
  const readyRef = useRef<Promise<void> | null>(null);

  useEffect(() => {
    let cancelled = false;
    readyRef.current = loadTurnstile().then((api) => {
      const el = containerRef.current;
      if (cancelled || !api || !el) return;
      apiRef.current = api;
      try {
        widgetIdRef.current = api.render(el, {
          sitekey: siteKey,
          execution: 'execute',
          appearance: 'interaction-only',
          language: 'fr',
          callback: (token: string) => settleRef.current?.(token),
          'error-callback': () => {
            settleRef.current?.(undefined);
            return true; // erreur gérée : pas de log bruyant de Cloudflare
          },
          'expired-callback': () => settleRef.current?.(undefined),
          'timeout-callback': () => settleRef.current?.(undefined),
        });
      } catch {
        widgetIdRef.current = null;
      }
    });
    return () => {
      cancelled = true;
      const api = apiRef.current;
      const id = widgetIdRef.current;
      if (api && id) {
        try {
          api.remove(id);
        } catch {
          /* widget déjà retiré */
        }
      }
      apiRef.current = null;
      widgetIdRef.current = null;
    };
  }, [siteKey]);

  /** Jeton frais à usage unique, ou `undefined` si Turnstile est indisponible / échoue. */
  const getToken = useCallback(async (): Promise<string | undefined> => {
    await readyRef.current;
    const api = apiRef.current;
    const id = widgetIdRef.current;
    if (!api || !id) return undefined;

    // Un défi précédent encore en cours est abandonné (résolu sans jeton).
    settleRef.current?.(undefined);

    return new Promise<string | undefined>((resolve) => {
      const timer = setTimeout(() => settle(undefined), tokenTimeoutMs);
      function settle(token: string | undefined) {
        clearTimeout(timer);
        if (settleRef.current === settle) settleRef.current = null;
        resolve(token);
      }
      settleRef.current = settle;
      try {
        api.reset(id);
        api.execute(id);
      } catch {
        settle(undefined);
      }
    });
  }, [tokenTimeoutMs]);

  return { containerRef, getToken };
}
